/**
 * One card on the grid (Home spec 2026-09-23 §4): a region named by the
 * widget's title, spanning its width in the page's stage (SA-1406). No minimum height (SA-1170): the grid
 * aligns rows to the top, so a card is as tall as its content. A card narrower than its minimum says so on
 * `data-view`, and its children are the widget's headline. In Customize
 * mode (Task 14) the card is also a dnd-kit sortable item — its own
 * `useSortable` call owns the drag transform and hands the grip's
 * listeners/attributes to `WidgetTools`.
 */
import type { ReactNode } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Card } from '@/components/ui'
import type { HomeWidgetEntry, HomeWidth } from '@/components/custom/homeWidgetTypes'
import { SPAN_CLASS, type HomeStage } from './homeFlow'
import { WidgetTools } from './WidgetTools'

export interface HomeWidgetCardProps {
  entry: HomeWidgetEntry
  width: HomeWidth
  /** The page's stage (SA-1406): it picks the span class. */
  stage: HomeStage
  /** The span to draw: undefined = the card's own width, null = inside a lane (no span class). */
  span?: HomeWidth | null
  /** Narrower than its minimum: the children are its headline. */
  headline?: boolean
  children: ReactNode
  /** Customize (Task 14): absent/false = read mode, no tools, drag disabled. */
  editing?: boolean
  canMoveUp?: boolean
  canMoveDown?: boolean
  onMove?: (direction: -1 | 1) => void
  onWidth?: (width: HomeWidth) => void
  onRemove?: () => void
}

export function HomeWidgetCard({
  entry, width, stage, span, headline = false, children, editing = false, canMoveUp = false, canMoveDown = false, onMove, onWidth, onRemove,
}: HomeWidgetCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: entry.id, disabled: !editing })
  // Fix round 1 finding 1: `CSS.Transform.toString` applies scaleX/scaleY =
  // newRect/oldRect under `rectSortingStrategy`, which distorts every
  // displaced card (and the dragged one) on a grid of 1/2/3-wide cards.
  // `CSS.Translate` is dnd-kit's documented choice for items of different
  // sizes — translate only, no scale.
  const style = { transform: CSS.Translate.toString(transform), transition }
  return (
    <Card ref={setNodeRef} style={style} role="region" aria-label={entry.title} data-widget={entry.id} data-view={headline ? 'headline' : 'full'}
      className={`flex flex-col gap-2.5 p-4 ${span === null ? '' : SPAN_CLASS[stage][span ?? width]}`}>
      {editing && onMove && onWidth && onRemove && (
        <WidgetTools entry={entry} width={width} canMoveUp={canMoveUp} canMoveDown={canMoveDown} onMove={onMove} onWidth={onWidth} onRemove={onRemove} handle={{ ...attributes, ...listeners }} />
      )}
      {children}
    </Card>
  )
}
